import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { T } from "react-translator-component";
import axios, { CancelTokenSource } from "axios";
import { convertUserImage } from "../../../util/utils";
import dayjs from "../../../util/dayjs";
import apiServices from "../../services/apiServices";
import SubmitButton from "../../../components/SubmitButton";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { colors } from "../../../common/colors";

interface Props {
  post: any;
  privateUsers: any[];
  onChange: (userId: number) => Promise<boolean>;
  onClose: () => void;
  readonly: boolean;
}

const AddPostUser = ({ post, privateUsers, onChange, onClose, readonly }: Props) => {
  const { admin } = useSelector((state: any) => state.adminAuth);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filteredOptions, setFilteredOptions] = useState<any[]>([]);
  const [searching, setSearching] = useState<boolean>(false);
  const cancelTokenRef = useRef<CancelTokenSource | null>(null);

  useEffect(() => {
    searchUsersOnline(searchTerm);
  }, [searchTerm]);

  const searchUsersOnline = async (search: string) => {
    if (search === "") {
      setFilteredOptions([]);
      return;
    }

    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel("Operation canceled due to new request.");
    }
    cancelTokenRef.current = axios.CancelToken.source();
    try {
      setSearching(true);

      const res = await apiServices.searchUsers(search, cancelTokenRef.current.token, true)

      const users = res.users
      const navigators = res.navigators
        .filter((navigator: any) => navigator.id !== admin.id)

      const userList = users.concat(navigators)
        .sort((a: any, b: any) => dayjs(a.created_at).valueOf() - dayjs(b.created_at).valueOf())

      setFilteredOptions(userList);
    } catch (error) {
      if (axios.isCancel(error)) {
        // pass
      } else {
        console.error("Error fetching search date:", error);
      }
    } finally {
      setSearching(false);
    }
  };

  const handleAddUser = async (user: any) => {
    const result = await onChange(user)
    if (result) {
      const searchResultUsers =
        filteredOptions.filter((searchUser: any) => searchUser.class_name !== user.class_name || searchUser.id !== user.id)
      setFilteredOptions(searchResultUsers)
    }
    return result
  }

  const displayOption = (user: any) => {
    const isExists = privateUsers.some((privateUser: any) =>
      privateUser.privater_type === user?.class_name && privateUser.privater_id === user?.id
    )

    return (
      <Box key={`${user?.class_name}_${user?.id}`} sx={styles.itemContainer}>
       <Avatar sx={styles.userImage} src={convertUserImage(user?.image)} />
        <p style={{ ...styles.nameText, fontWeight: "normal", textAlign: "left" }}>{user?.nickname}</p>
        <SubmitButton
          validate={async () => true}
          onSubmit={() => handleAddUser(user)}
        >
          {(!readonly && !isExists) && <AddCircleOutlineIcon sx={styles.addIcon} />}
        </SubmitButton>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={styles.headerBox}>
        <Box sx={styles.rowCenter}>
          <p style={styles.title}>{post.title}</p>
        </Box>
      </Box>
      <Box sx={styles.searchUserContainer}>
        <Box sx={styles.searchBoxStyle}>
          <Typography sx={styles.description}>{T("mng_add_post_private_user_desc")}</Typography>
          <TextField
            sx={{ width: "100%" }}
            id="outlined-basic"
            label={T("mng_search_users_label")}
            variant="outlined"
            placeholder={`${T("mng_search_users_label")}`}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
            }}
            InputProps={{
              endAdornment: <InputAdornment position="end">{searching && <CircularProgress size={20} />}</InputAdornment>,
            }}
          />
        </Box>
        <Typography style={styles.titleSeprator}>{T("mng_can_view_post_user")}</Typography>
        <Box style={styles.userList}>
          {filteredOptions.map((item: any) => (
            displayOption(item)
          ))}
        </Box>
      </Box>

      <Stack sx={styles.modalButtonContainer}>
        <Button
          onClick={onClose}
          style={styles.dialogButton}
          size={"large"}
          variant="outlined"
        >
          {T('mng_close')}
        </Button>
      </Stack>
    </Box>
  );
};

const styles = {
  headerBox: {
    padding: "1px 16px",
    paddingTop: "20px",
    borderBottom: `1px solid ${colors.farm_gray15}`,
  },
  searchUserContainer: {
    padding: "16px",
  },
  description: {
    mb: 1,
    height: "24px",
  },
  userList: {
    minHeight: "100px",
    maxHeight: "calc(100vh*0.9*0.7 - 171px)",
    overflowY: "auto" as const,
  },
  userImage: {
    width: "30px",
    height: "30px",
    borderRadius: "50%",
    objectFit: "cover",
    marginLeft: "10px",
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
    margin: "0 auto",
    height: "20px"
  },
  nameText: {
    fontSize: 14,
    fontWeight: 600,
    marginLeft: "10px",
    marginBottom: "0px",
    flex: 1,
  },
  itemContainer: {
    display: "flex",
    alignItems: "center",
    marginRight: "16px",
    marginBottom: "16px",
    borderBottomStyle: "solid" as const,
    borderBottomColor: colors.farm_gray100,
    borderBottomWidth: 1,
    padding: "5px 0px",
  },
  rowCenter: {
    margin: "10px 0px",
    display: "flex",
    alignItems: "center",
  },
  titleSeprator: {
    borderBottomColor: colors.farm_green500,
    borderBottomWidth: 2,
    borderBottomStyle: "solid" as const,
    fontSize: 14,
    fontFamily: "mplus-1c-bold",
    marginTop: 10,
    marginBottom: 6,
    height: "25px",
  },
  addIcon: {
    color: colors.farm_green500,
    fontSize: "35px",
    cursor: "pointer",
    marginLeft: "10px",
  },
  searchBoxStyle: { width: "100%", height: "100px" },
  modalButtonContainer: {
    displat: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: "20px",
    marginBottom: "20px",
    width: "100%"
  },
  dialogButton: {
    width: "50%",
    minWitdh: "100px",
    alignSelf: "center",
    display: "flex",
    border: "3px solid",
    borderRadius: "10px",
    borderColor: colors.farm_blue200,
    color: colors.farm_black
  },

};
export default AddPostUser;
